import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ApiServices from "../../Services/ApiServices";
import MiniLoader from "../../Shared/MiniLoader";
import Header from "../../Shared/Header";
import MobileBlocker from "../../Shared/MobileBlocker";
import botImage from "../../../assets/images/bot.png";
import "./StoryLearnComponent.css";

const AI_LOADER_COLOR = "#6c5ce7";

/* Mario-style physics & sizes */
const PHYSICS = {
  GRAVITY: 0.48,
  JUMP_V: -8.7,
  ACCEL: 0.22,
  MAX_VX: 1.95,
  FRICTION: 0.78,
  MAX_FALL: 7.2,
};
const HERO_W = 32;
const HERO_H = 42;
const GROUND_H = 36;
const BLOCK_SIZE = 44;
const CHALLENGE_TIME = 60;

const STRINGS = {
  ar: {
    pageTitle: "تعلم بالقصة مع رفيق",
    pageSubtitle: "ابنِ مغامرة تعليمية من موضوعك المفضل",
    topic: "الموضوع",
    subject: "المادة",
    grade: "الصف",
    heroName: "اسم البطل",
    language: "اللغة",
    sceneCount: "عدد المشاهد",
    loading: "رفيق يصنع قصتك...",
    submit: "إنشاء القصة",
    retry: "إعادة المحاولة",
    introCta: "ابدأ المغامرة",
    welcome: (hero, world) => `مرحباً يا ${hero} في ${world}`,
    topicBadge: "الموضوع",
    continueReading: "تابع",
    skip: "تخطي",
    challenge: "التحدي",
    continue: "متابعة",
    tryAgain: "أعد المحاولة",
    next: "التالي",
    correct: "إجابة صحيحة!",
    wrong: "إجابة خاطئة",
    points: "نقاط",
    xpLabel: "XP",
    completeTitle: "لقد أكملت المغامرة!",
    finalScore: "النتيجة النهائية",
    moral: "الدرس المستفاد",
    playAgain: "إعادة اللعب",
    backToForm: "العودة",
    required: "هذا الحقل مطلوب",
    xpGain: (n) => `+${n} XP`,
    sceneOf: (a, b) => `المشهد ${a} من ${b}`,
    livesLabel: "أرواح",
    keyEnter: "Enter",
    keySpace: "Space",
    walkHint: "السهم يمين/يسار للتحرك • Space أو السهم لأعلى للقفز • اقفز على ؟ للإجابة",
    storyHint: "السهم يمين للسير نحو العلم 🚩 • أكمل القصة وادخل المرحلة",
    needToReadFirst: "أكمل القصة أولاً",
    pickedAnswer: (n) => `قفزت على الإجابة ${n}`,
    questionPrefix: "السؤال:",
    timeLabel: "الوقت",
    timeUp: "انتهى الوقت!",
  },
  en: {
    pageTitle: "Story Learn with Rafiq",
    pageSubtitle: "Build a learning adventure from your favorite topic",
    topic: "Topic",
    subject: "Subject",
    grade: "Grade",
    heroName: "Hero name",
    language: "Language",
    sceneCount: "Number of scenes",
    loading: "Rafiq is crafting your story...",
    submit: "Create story",
    retry: "Try again",
    introCta: "Start the adventure",
    welcome: (hero, world) => `Welcome, ${hero}, to ${world}`,
    topicBadge: "Topic",
    continueReading: "Continue",
    skip: "Skip",
    challenge: "Challenge",
    continue: "Continue",
    tryAgain: "Try again",
    next: "Next",
    correct: "Correct!",
    wrong: "Not quite",
    points: "Points",
    xpLabel: "XP",
    completeTitle: "You finished the adventure!",
    finalScore: "Final score",
    moral: "The lesson",
    playAgain: "Play again",
    backToForm: "Back",
    required: "This field is required",
    xpGain: (n) => `+${n} XP`,
    sceneOf: (a, b) => `Scene ${a} of ${b}`,
    livesLabel: "Lives",
    keyEnter: "Enter",
    keySpace: "Space",
    walkHint: "←/→ to move • Space or ↑ to jump • Hit a ? block to answer",
    storyHint: "Run right toward the flag 🚩 • finish the story to enter the challenge",
    needToReadFirst: "Finish the story first",
    pickedAnswer: (n) => `Hit answer ${n}`,
    questionPrefix: "Question:",
    timeLabel: "Time",
    timeUp: "Time's up!",
  },
};

function normalizePayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.meta && Array.isArray(raw.scenes)) return raw;
  if (raw.data?.meta && Array.isArray(raw.data?.scenes)) return raw.data;
  return null;
}

function shuffleSceneChoices(payload) {
  if (!payload?.scenes?.length) return payload;
  const scenes = payload.scenes.map((scene) => {
    const choices = Array.isArray(scene?.choices) ? [...scene.choices] : [];
    if (choices.length <= 1) return scene;

    const originalCorrect = Number(scene.correct_index);
    const indexed = choices.map((choice, index) => ({ choice, index }));

    // Fisher-Yates shuffle for better random distribution.
    for (let i = indexed.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
    }

    const shuffledChoices = indexed.map((x) => x.choice);
    const nextCorrect = indexed.findIndex((x) => x.index === originalCorrect);

    return {
      ...scene,
      choices: shuffledChoices,
      correct_index: nextCorrect >= 0 ? nextCorrect : 0,
    };
  });

  return {
    ...payload,
    scenes,
  };
}

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

let coinIdSeq = 0;

const StoryLearnComponent = () => {
  const isPhoneDevice = () => {
    if (typeof window === "undefined") return false;
    const smallViewport = window.innerWidth <= 767;
    const ua = window.navigator?.userAgent || "";
    const phoneUa = /Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua);
    const tabletUa = /iPad|Tablet/i.test(ua);
    return smallViewport && phoneUa && !tabletUa;
  };

  const [phase, setPhase] = useState("form");
  const [isPhoneOnly, setIsPhoneOnly] = useState(isPhoneDevice);
  const [gamePayload, setGamePayload] = useState(null);

  const [topic, setTopic] = useState("");
  const [subject, setSubject] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [userClasses, setUserClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState(null);
  const [heroName, setHeroName] = useState("");
  const [language, setLanguage] = useState("Arabic");
  const [sceneCount, setSceneCount] = useState(5);

  const [formErrors, setFormErrors] = useState({});
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const [uiLang, setUiLang] = useState("ar");
  const t = STRINGS[uiLang] || STRINGS.ar;

  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [screen, setScreen] = useState("intro");
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [typewriterIndex, setTypewriterIndex] = useState(0);

  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(false);
  const [rewardXpEarned, setRewardXpEarned] = useState(0);
  const [screenOpacity, setScreenOpacity] = useState(1);
  const [timeLeft, setTimeLeft] = useState(CHALLENGE_TIME);

  // Mario game state (rendered every frame)
  const [heroState, setHeroState] = useState({
    x: 60,
    y: 200,
    dir: 1,
    walkPhase: 0,
    jumping: false,
  });
  const [blockStates, setBlockStates] = useState([
    { hit: false },
    { hit: false },
    { hit: false },
    { hit: false },
  ]);
  const [coins, setCoins] = useState([]);
  const [flagReached, setFlagReached] = useState(false);
  const [arenaShake, setArenaShake] = useState(false);
  const [flashLevel, setFlashLevel] = useState(null); // 'good' | 'bad'

  // Refs (not reactive)
  const arenaRef = useRef(null);
  const heroRef = useRef({
    x: 60,
    y: 200,
    vx: 0,
    vy: 0,
    onGround: true,
    dir: 1,
    walkPhase: 0,
  });
  const blocksRef = useRef([]); // [{ x, y, w, h, hit, idx }]
  const flagXRef = useRef(0);
  const obstaclesRef = useRef([]); // small bricks to jump over (story flair)
  const platformsRef = useRef([]); // [{ x, y, w, h }]
  const keysRef = useRef({ left: false, right: false, jump: false, jumpHeld: false });
  const rafRef = useRef(null);
  const isLockedRef = useRef(false);
  const screenRef = useRef("intro");
  const advanceRef = useRef(() => {});
  const flagReachedRef = useRef(false);
  const flagAdvanceTimerRef = useRef(null);
  const typewriterDoneRef = useRef(false);
  const typewriterSkipRef = useRef(false);
  const narrativeFullRef = useRef("");
  const goToChallengeRef = useRef(() => {});
  const jumpSfxRef = useRef(null);
  const itemSfxRef = useRef(null);
  const hurrySfxRef = useRef(null);
  const gameOverSfxRef = useRef(null);
  const hurryPlayedRef = useRef(false);

  const meta = gamePayload?.meta;
  const scenes = gamePayload?.scenes ?? [];
  const currentScene = scenes[currentSceneIndex] ?? null;
  const narrativeFull = currentScene?.narrative ?? "";

  const progressPct = scenes.length
    ? Math.round(((currentSceneIndex + 1) / scenes.length) * 100)
    : 0;

  const starCount = useMemo(() => {
    const total = scenes.length || 1;
    const ratio = correctAnswers / total;
    if (ratio >= 0.8) return 3;
    if (ratio >= 0.5) return 2;
    return 1;
  }, [correctAnswers, scenes.length]);

  useEffect(() => {
    let mounted = true;
    const fetchUserClasses = async () => {
      setClassesLoading(true);
      setClassesError(null);
      try {
        const data = await ApiServices.get(
          "Lookup/UserClasses",
          {},
          200000,
          {
            headers: {
              "Accept": "*/*",
              "Accept-Language": "en",
            },
          }
        );
        const list = Array.isArray(data)
          ? data
              .map((x) => ({
                id: x?.id,
                name: x?.name,
              }))
              .filter((x) => x.id != null && x.name)
          : [];
        if (!mounted) return;
        setUserClasses(list);
        setSelectedClassId((prev) =>
          prev || (list.length ? String(list[0].id) : "")
        );
      } catch (_) {
        if (!mounted) return;
        setClassesError(
          uiLang === "en"
            ? "Failed to load classes. Please try again."
            : "تعذر تحميل الصفوف، حاول مرة أخرى."
        );
        setUserClasses([]);
      } finally {
        if (mounted) setClassesLoading(false);
      }
    };
    fetchUserClasses();
    return () => {
      mounted = false;
    };
  }, [uiLang]);

  useEffect(() => {
    const handleViewport = () => setIsPhoneOnly(isPhoneDevice());
    handleViewport();
    window.addEventListener("resize", handleViewport);
    window.addEventListener("orientationchange", handleViewport);
    return () => {
      window.removeEventListener("resize", handleViewport);
      window.removeEventListener("orientationchange", handleViewport);
    };
  }, []);

  // Stable cloud / hill seeds
  const clouds = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => ({
        id: i,
        top: 8 + Math.random() * 30,
        left: 5 + i * 22 + Math.random() * 6,
        scale: 0.7 + Math.random() * 0.7,
        delay: -Math.random() * 30,
      })),
    [currentSceneIndex, screen]
  );
  const hills = useMemo(
    () =>
      Array.from({ length: 3 }, (_, i) => ({
        id: i,
        left: i * 38 + Math.random() * 8,
        scale: 0.85 + Math.random() * 0.5,
        hue: 110 + Math.random() * 20,
      })),
    [currentSceneIndex, screen]
  );

  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  useEffect(() => {
    setScreenOpacity(0);
    const id = window.setTimeout(() => setScreenOpacity(1), 50);
    return () => window.clearTimeout(id);
  }, [screen]);

  // Typewriter for story narrative
  useEffect(() => {
    if (phase !== "game" || screen !== "story") return undefined;
    const text = narrativeFull;
    setTypewriterIndex(0);
    typewriterSkipRef.current = false;
    if (!text) return undefined;
    let idx = 0;
    const interval = setInterval(() => {
      if (typewriterSkipRef.current) {
        setTypewriterIndex(text.length);
        clearInterval(interval);
        return;
      }
      idx += 1;
      setTypewriterIndex(idx);
      if (idx >= text.length) clearInterval(interval);
    }, 28);
    return () => clearInterval(interval);
  }, [phase, screen, currentSceneIndex, narrativeFull]);

  const skipTypewriter = useCallback(() => {
    typewriterSkipRef.current = true;
    setTypewriterIndex(narrativeFullRef.current?.length || 0);
  }, []);

  const validateForm = () => {
    const vt = STRINGS[language === "English" ? "en" : "ar"];
    const err = {};
    if (!topic.trim()) err.topic = vt.required;
    if (!subject.trim()) err.subject = vt.required;
    if (!selectedClassId) err.grade = vt.required;
    if (!heroName.trim()) err.heroName = vt.required;
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const resetGameSession = useCallback((fullReset) => {
    setCurrentSceneIndex(0);
    setScreen("intro");
    setScore(0);
    setLives(3);
    setCorrectAnswers(0);
    setAttemptCount(0);
    setSelectedChoice(null);
    setIsLocked(false);
    setTypewriterIndex(0);
    setLastAnswerCorrect(false);
    setRewardXpEarned(0);
    setBlockStates([{ hit: false }, { hit: false }, { hit: false }, { hit: false }]);
    setCoins([]);
    setFlagReached(false);
    flagReachedRef.current = false;
    setTimeLeft(CHALLENGE_TIME);
    hurryPlayedRef.current = false;
    if (fullReset) {
      setGamePayload(null);
      setPhase("form");
    }
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    if (!validateForm()) return;
    const nextLang = language === "English" ? "en" : "ar";
    setUiLang(nextLang);
    setApiLoading(true);
    try {
      const selectedClass = userClasses.find(
        (x) => String(x.id) === String(selectedClassId)
      );
      const body = {
        topic: topic.trim(),
        subject: subject.trim(),
        grade: selectedClass?.name || selectedClassId,
        class_id: selectedClass?.id,
        hero_name: heroName.trim(),
        language,
        scene_count: Number(sceneCount),
      };
      const raw = await ApiServices.post("Rafiq/Student/StoryLearn", body,false,600000);
      const normalized = normalizePayload(raw);
      if (!normalized?.scenes?.length) {
        throw new Error(
          nextLang === "en"
            ? "Invalid response from server."
            : "استجابة غير صالحة من الخادم."
        );
      }
      setGamePayload(shuffleSceneChoices(normalized));
      setPhase("game");
      resetGameSession(false);
    } catch (err) {
      const msg =
        err?.message ||
        err?.msg ||
        (typeof err === "string" ? err : null) ||
        (nextLang === "en" ? "Something went wrong." : "حدث خطأ ما.");
      setApiError(msg);
    } finally {
      setApiLoading(false);
    }
  };

  const handleReplaySameStory = () => {
    resetGameSession(false);
  };

  const handleBackToForm = () => {
    resetGameSession(true);
    setApiError(null);
  };

  const goToChallenge = useCallback(() => {
    setSelectedChoice(null);
    setIsLocked(false);
    setBlockStates([{ hit: false }, { hit: false }, { hit: false }, { hit: false }]);
    setCoins([]);
    setFlagReached(false);
    flagReachedRef.current = false;
    setTimeLeft(CHALLENGE_TIME);
    hurryPlayedRef.current = false;
    setScreen("challenge");
  }, []);

  useEffect(() => {
    narrativeFullRef.current = narrativeFull;
  }, [narrativeFull]);

  useEffect(() => {
    goToChallengeRef.current = goToChallenge;
  }, [goToChallenge]);

  const playSfx = useCallback((audioRef) => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.currentTime = 0;
      const maybePromise = audio.play();
      if (maybePromise?.catch) {
        maybePromise.catch(() => {});
      }
    } catch (_) {
      // Ignore browser audio-play restrictions/errors.
    }
  }, []);

  useEffect(() => {
    const jumpAudio = new Audio("/assets/Sound/jump.wav");
    jumpAudio.preload = "auto";
    jumpAudio.volume = 0.45;
    jumpSfxRef.current = jumpAudio;

    const itemAudio = new Audio("/assets/Sound/item.wav");
    itemAudio.preload = "auto";
    itemAudio.volume = 0.5;
    itemSfxRef.current = itemAudio;

    const hurryAudio = new Audio("/assets/Sound/hurryup.wav");
    hurryAudio.preload = "auto";
    hurryAudio.volume = 0.55;
    hurrySfxRef.current = hurryAudio;

    const gameOverAudio = new Audio("/assets/Sound/gameover.wav");
    gameOverAudio.preload = "auto";
    gameOverAudio.volume = 0.6;
    gameOverSfxRef.current = gameOverAudio;

    return () => {
      if (jumpSfxRef.current) {
        jumpSfxRef.current.pause();
        jumpSfxRef.current = null;
      }
      if (itemSfxRef.current) {
        itemSfxRef.current.pause();
        itemSfxRef.current = null;
      }
      if (hurrySfxRef.current) {
        hurrySfxRef.current.pause();
        hurrySfxRef.current = null;
      }
      if (gameOverSfxRef.current) {
        gameOverSfxRef.current.pause();
        gameOverSfxRef.current = null;
      }
    };
  }, []);

  // Choice handler (called when player jumps into a ? block, presses 1-4, or clicks)
  const handleChoice = useCallback(
    (index) => {
      if (isLockedRef.current || !currentScene) return;
      const choices = currentScene.choices || [];
      if (index == null || index < 0 || index >= choices.length) return;
      playSfx(itemSfxRef);
      setIsLocked(true);
      isLockedRef.current = true;
      setSelectedChoice(index);
      const correctIdx = Number(currentScene.correct_index) || 0;
      const isCorrect = index === correctIdx;
      setLastAnswerCorrect(isCorrect);

      // Mark this block as hit
      setBlockStates((prev) =>
        prev.map((b, i) => (i === index ? { hit: true } : b))
      );

      // Coin pop animation
      const blk = blocksRef.current[index];
      if (blk) {
        coinIdSeq += 1;
        const id = coinIdSeq;
        setCoins((prev) => [
          ...prev,
          {
            id,
            x: blk.x + blk.w / 2 - 10,
            y: blk.y - 6,
            label: isCorrect ? "+10" : "?",
            kind: isCorrect ? "good" : "bad",
          },
        ]);
        window.setTimeout(() => {
          setCoins((prev) => prev.filter((c) => c.id !== id));
        }, 950);
      }

      setFlashLevel(isCorrect ? "good" : "bad");
      window.setTimeout(() => setFlashLevel(null), 600);

      if (!isCorrect) {
        setArenaShake(true);
        window.setTimeout(() => setArenaShake(false), 480);
      }

      window.setTimeout(() => {
        if (isCorrect) {
          if (attemptCount === 0) setCorrectAnswers((c) => c + 1);
        } else {
          setAttemptCount((a) => a + 1);
          setLives((l) => Math.max(0, l - 1));
        }
        setScreen("result");
      }, 900);
    },
    [currentScene, attemptCount, playSfx]
  );

  // Time runs out → treat as a wrong answer (lose a life, shake, go to result)
  const handleTimeUp = useCallback(() => {
    playSfx(gameOverSfxRef);
    setIsLocked(true);
    setSelectedChoice(null);
    setLastAnswerCorrect(false);
    setFlashLevel("bad");
    setArenaShake(true);
    window.setTimeout(() => setFlashLevel(null), 600);
    window.setTimeout(() => setArenaShake(false), 480);
    window.setTimeout(() => {
      setAttemptCount((a) => a + 1);
      setLives((l) => Math.max(0, l - 1));
      setScreen("result");
    }, 700);
  }, [playSfx]);

  // 60-second countdown — runs only on the challenge screen, before answering.
  useEffect(() => {
    if (phase !== "game") return undefined;
    if (screen !== "challenge") return undefined;
    if (isLocked) return undefined;
    if (timeLeft <= 0) {
      handleTimeUp();
      return undefined;
    }
    const id = window.setInterval(() => {
      setTimeLeft((t2) => Math.max(0, t2 - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, screen, isLocked, timeLeft, handleTimeUp]);

  useEffect(() => {
    if (phase !== "game" || screen !== "challenge" || isLocked) return;
    if (timeLeft === 10 && !hurryPlayedRef.current) {
      hurryPlayedRef.current = true;
      playSfx(hurrySfxRef);
    }
  }, [phase, screen, isLocked, timeLeft, playSfx]);

  const handleResultAction = () => {
    if (lastAnswerCorrect) {
      const xp = attemptCount === 0 ? 10 : 5;
      setRewardXpEarned(xp);
      setScore((s) => s + xp);
      setScreen("reward");
      return;
    }
    if (lives <= 0) {
      // Hearts are fully consumed: restart the generated story from scene 1.
      resetGameSession(false);
      return;
    }
    if (attemptCount < 2 && lives > 0) {
      setSelectedChoice(null);
      setIsLocked(false);
      setBlockStates([
        { hit: false },
        { hit: false },
        { hit: false },
        { hit: false },
      ]);
      setCoins([]);
      setTimeLeft(CHALLENGE_TIME);
      hurryPlayedRef.current = false;
      setScreen("challenge");
      return;
    }
    setRewardXpEarned(0);
    setScreen("reward");
  };

  const handleComplete = () => {
    setScreen("complete");
  };

  const handleRewardNext = () => {
    if (currentSceneIndex >= scenes.length - 1) {
      handleComplete();
      return;
    }
    setCurrentSceneIndex((i) => i + 1);
    setAttemptCount(0);
    setSelectedChoice(null);
    setIsLocked(false);
    setTypewriterIndex(0);
    setRewardXpEarned(0);
    setBlockStates([{ hit: false }, { hit: false }, { hit: false }, { hit: false }]);
    setCoins([]);
    setFlagReached(false);
    flagReachedRef.current = false;
    setTimeLeft(CHALLENGE_TIME);
    hurryPlayedRef.current = false;
    setScreen("story");
  };

  const typewriterDone =
    !narrativeFull || typewriterIndex >= narrativeFull.length;

  useEffect(() => {
    typewriterDoneRef.current = typewriterDone;
  }, [typewriterDone]);

  useEffect(() => {
    if (phase !== "game") return;
    if (screen !== "story" && screen !== "challenge") return;
    const active = document.activeElement;
    if (
      active &&
      active !== document.body &&
      typeof active.blur === "function"
    ) {
      active.blur();
    }
  }, [phase, screen, currentSceneIndex]);

  // Always-fresh advance handler (used by overlay screens)
  advanceRef.current = () => {
    if (screen === "intro") {
      setScreen("story");
    } else if (screen === "story") {
      if (!typewriterDone) skipTypewriter();
    } else if (screen === "result") {
      handleResultAction();
    } else if (screen === "reward") {
      handleRewardNext();
    } else if (screen === "complete") {
      handleReplaySameStory();
    }
  };

  // -------- Mario level init & physics loop --------
  useEffect(() => {
    if (
      phase !== "game" ||
      (screen !== "story" && screen !== "challenge")
    ) {
      // tear down loop
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      keysRef.current = { left: false, right: false, jump: false, jumpHeld: false };
      if (flagAdvanceTimerRef.current) {
        clearTimeout(flagAdvanceTimerRef.current);
        flagAdvanceTimerRef.current = null;
      }
      return undefined;
    }

    const arena = arenaRef.current;
    if (!arena) return undefined;

    const initLevel = () => {
      const W = arena.clientWidth;
      const H = arena.clientHeight;
      const groundY = H - GROUND_H - HERO_H;

      heroRef.current = {
        x: 60,
        y: groundY,
        vx: 0,
        vy: 0,
        onGround: true,
        dir: 1,
        walkPhase: 0,
      };
      setHeroState({
        x: 60,
        y: groundY,
        dir: 1,
        walkPhase: 0,
        jumping: false,
        vx: 0,
        vy: 0,
      });

      if (screen === "challenge") {
        // Spread 4 ? blocks across the level
        const startX = W * 0.18;
        const spacing = (W * 0.7) / 3;
        // Keep challenge blocks reachable after slowdown on all sizes.
        // Raise tablets a bit more so ? blocks appear clearly above Mario.
        const blockLift = W >= 992 ? 62 : W >= 768 ? 74 : 56;
        const blockY = H - GROUND_H - HERO_H - blockLift;
        blocksRef.current = [0, 1, 2, 3].map((i) => ({
          idx: i,
          x: startX + i * spacing,
          y: blockY,
          w: BLOCK_SIZE,
          h: BLOCK_SIZE,
          hit: false,
        }));
        // No flag in challenge, no obstacles
        flagXRef.current = 0;
        obstaclesRef.current = [];
        // Optional small platform under blocks 2 & 3 (visual flair)
        platformsRef.current = [
          {
            x: blocksRef.current[1].x + BLOCK_SIZE * 1.2,
            y: blockY + BLOCK_SIZE + 28,
            w: BLOCK_SIZE * 1.6,
            h: 14,
          },
        ];
      } else {
        // Story: a flag at the right end + a small brick obstacle
        flagXRef.current = W - 60;
        obstaclesRef.current = [
          {
            x: Math.max(W * 0.55, 240),
            y: H - GROUND_H - 28,
            w: 28,
            h: 28,
          },
        ];
        platformsRef.current = [];
        blocksRef.current = [];
      }

      setFlagReached(false);
      flagReachedRef.current = false;
      setBlockStates([
        { hit: false },
        { hit: false },
        { hit: false },
        { hit: false },
      ]);
    };

    initLevel();

    const tick = () => {
      const a = arenaRef.current;
      if (!a) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const W = a.clientWidth;
      const H = a.clientHeight;
      const groundY = H - GROUND_H - HERO_H;

      const k = keysRef.current;
      const h = heroRef.current;

      // Horizontal
      if (k.left && !k.right) {
        h.vx -= PHYSICS.ACCEL;
        h.dir = -1;
      } else if (k.right && !k.left) {
        h.vx += PHYSICS.ACCEL;
        h.dir = 1;
      } else {
        h.vx *= PHYSICS.FRICTION;
        if (Math.abs(h.vx) < 0.05) h.vx = 0;
      }
      h.vx = clamp(h.vx, -PHYSICS.MAX_VX, PHYSICS.MAX_VX);

      // Jump (single press while grounded) — Mario-style with variable height
      if (k.jump && h.onGround && !k.jumpHeld) {
        playSfx(jumpSfxRef);
        h.vy = PHYSICS.JUMP_V;
        h.onGround = false;
        k.jumpHeld = true;
        k.jumpCut = false;
      }

      // Variable jump cut: release early while still rising → cut velocity
      if (!k.jump && !h.onGround && !k.jumpCut && h.vy < 0) {
        h.vy *= 0.45;
        k.jumpCut = true;
      }

      // Gravity (lighter while ascending with jump held — Mario floaty arc)
      const gravity =
        !h.onGround && k.jump && h.vy < 0 && !k.jumpCut
          ? PHYSICS.GRAVITY * 0.7
          : PHYSICS.GRAVITY;
      h.vy = Math.min(h.vy + gravity, PHYSICS.MAX_FALL);

      // Integrate X
      const prevX = h.x;
      h.x += h.vx;

      // Story: invisible barrier just before flag if narrative isn't done
      if (
        screenRef.current === "story" &&
        !typewriterDoneRef.current &&
        flagXRef.current > 0
      ) {
        const wall = flagXRef.current - 24;
        if (h.x + HERO_W >= wall) {
          h.x = wall - HERO_W;
          h.vx = 0;
        }
      }

      // Bounds
      if (h.x < 4) {
        h.x = 4;
        h.vx = 0;
      }
      if (h.x > W - HERO_W - 4) {
        h.x = W - HERO_W - 4;
        h.vx = 0;
      }

      // Obstacles (story bricks): basic AABB resolve in X
      obstaclesRef.current.forEach((b) => {
        const overlapX = h.x + HERO_W > b.x && h.x < b.x + b.w;
        const overlapY = h.y + HERO_H > b.y && h.y < b.y + b.h;
        if (overlapX && overlapY) {
          if (prevX + HERO_W <= b.x) {
            h.x = b.x - HERO_W;
            h.vx = 0;
          } else if (prevX >= b.x + b.w) {
            h.x = b.x + b.w;
            h.vx = 0;
          }
        }
      });

      // Integrate Y
      const prevY = h.y;
      h.y += h.vy;

      // Ground
      if (h.y >= groundY) {
        h.y = groundY;
        h.vy = 0;
        h.onGround = true;
      } else {
        h.onGround = false;
      }

      // Obstacles (story): land on top
      obstaclesRef.current.forEach((b) => {
        const overlapX = h.x + HERO_W > b.x && h.x < b.x + b.w;
        const overlapY = h.y + HERO_H > b.y && h.y < b.y + b.h;
        if (overlapX && overlapY) {
          if (prevY + HERO_H <= b.y) {
            h.y = b.y - HERO_H;
            h.vy = 0;
            h.onGround = true;
          } else if (prevY >= b.y + b.h) {
            h.y = b.y + b.h;
            h.vy = 1;
          }
        }
      });

      // Platforms (challenge only flair, top-only collision)
      platformsRef.current.forEach((p) => {
        const overlapX = h.x + HERO_W > p.x && h.x < p.x + p.w;
        const fallingThrough = h.vy > 0;
        if (
          overlapX &&
          fallingThrough &&
          prevY + HERO_H <= p.y &&
          h.y + HERO_H >= p.y &&
          h.y + HERO_H <= p.y + p.h + 6
        ) {
          h.y = p.y - HERO_H;
          h.vy = 0;
          h.onGround = true;
        }
      });

      // ? blocks (challenge): hit from below
      if (
        screenRef.current === "challenge" &&
        !isLockedRef.current
      ) {
        for (let i = 0; i < blocksRef.current.length; i++) {
          const b = blocksRef.current[i];
          if (b.hit) continue;
          const overlapX = h.x + HERO_W > b.x && h.x < b.x + b.w;
          const overlapY = h.y + HERO_H > b.y && h.y < b.y + b.h;
          if (overlapX && overlapY) {
            const heroTop = h.y;
            const blockBottom = b.y + b.h;
            const cameFromBelow =
              prevY >= blockBottom - 2 && h.vy < 0 && heroTop < blockBottom;
            if (cameFromBelow) {
              h.y = blockBottom + 1;
              h.vy = 3;
              b.hit = true;
              handleChoice(i);
              break;
            } else if (prevY + HERO_H <= b.y) {
              // Land on top
              h.y = b.y - HERO_H;
              h.vy = 0;
              h.onGround = true;
            } else {
              // Side push
              if (prevX + HERO_W <= b.x) {
                h.x = b.x - HERO_W;
                h.vx = 0;
              } else if (prevX >= b.x + b.w) {
                h.x = b.x + b.w;
                h.vx = 0;
              }
            }
          }
        }
      }

      // Flag (story): reach end → finalize narrative & advance
      if (
        screenRef.current === "story" &&
        !flagReachedRef.current &&
        flagXRef.current > 0
      ) {
        if (h.x + HERO_W >= flagXRef.current && typewriterDoneRef.current) {
          flagReachedRef.current = true;
          setFlagReached(true);
          if (flagAdvanceTimerRef.current) {
            clearTimeout(flagAdvanceTimerRef.current);
          }
          flagAdvanceTimerRef.current = window.setTimeout(() => {
            goToChallengeRef.current?.();
          }, 1100);
        }
      }

      // Walk phase animation
      if (h.onGround && Math.abs(h.vx) > 0.4) {
        h.walkPhase = (h.walkPhase + Math.abs(h.vx) * 0.18) % (Math.PI * 4);
      }

      setHeroState({
        x: h.x,
        y: h.y,
        dir: h.dir,
        walkPhase: h.walkPhase,
        jumping: !h.onGround,
        vx: h.vx,
        vy: h.vy,
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      keysRef.current = { left: false, right: false, jump: false, jumpHeld: false };
      if (flagAdvanceTimerRef.current) {
        clearTimeout(flagAdvanceTimerRef.current);
        flagAdvanceTimerRef.current = null;
      }
    };
  }, [phase, screen, currentSceneIndex, handleChoice, playSfx]);

  // -------- Keyboard --------
  useEffect(() => {
    if (phase !== "game") return undefined;

    const isLeftKey = (e) =>
      e.code === "ArrowLeft" ||
      e.key === "ArrowLeft" ||
      e.keyCode === 37 ||
      e.which === 37 ||
      e.code === "KeyA" ||
      e.key === "a" ||
      e.key === "A";

    const isRightKey = (e) =>
      e.code === "ArrowRight" ||
      e.key === "ArrowRight" ||
      e.keyCode === 39 ||
      e.which === 39 ||
      e.code === "KeyD" ||
      e.key === "d" ||
      e.key === "D";

    const isJumpKey = (e) =>
      e.code === "Space" ||
      e.code === "ArrowUp" ||
      e.key === " " ||
      e.key === "Spacebar" ||
      e.key === "ArrowUp" ||
      e.keyCode === 32 ||
      e.keyCode === 38 ||
      e.which === 32 ||
      e.which === 38 ||
      e.code === "KeyW" ||
      e.key === "w" ||
      e.key === "W";

    const setMoveKey = (e, value) => {
      const ks = keysRef.current;
      let used = false;
      if (isLeftKey(e)) {
        ks.left = value;
        used = true;
      }
      if (isRightKey(e)) {
        ks.right = value;
        used = true;
      }
      if (isJumpKey(e)) {
        ks.jump = value;
        if (!value) ks.jumpHeld = false;
        used = true;
      }
      return used;
    };

    const onKeyDown = (e) => {
      const target = e.target;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        return;
      }

      const sc = screenRef.current;

      if (sc === "story" || sc === "challenge") {
        const used = setMoveKey(e, true);
        if (used) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (sc === "story" && (e.code === "Enter" || e.key === "Enter")) {
          e.preventDefault();
          if (!typewriterDoneRef.current) {
            typewriterSkipRef.current = true;
            setTypewriterIndex(narrativeFullRef.current.length);
          } else if (!flagReachedRef.current) {
            goToChallengeRef.current?.();
          }
        }
        return;
      }

      // overlay screens
      if (
        e.code === "Space" ||
        e.code === "Enter" ||
        e.key === " " ||
        e.key === "Enter"
      ) {
        e.preventDefault();
        advanceRef.current?.();
      }
    };

    const onKeyUp = (e) => {
      setMoveKey(e, false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [phase, handleChoice]);

  // Touch on-screen controls (mobile)
  const pressKey = (k, val) => {
    keysRef.current[k] = val;
    if (k === "jump" && !val) keysRef.current.jumpHeld = false;
  };

  const holdHandlers = (k) => ({
    onMouseDown: (e) => {
      e.preventDefault();
      pressKey(k, true);
    },
    onMouseUp: () => pressKey(k, false),
    onMouseLeave: () => pressKey(k, false),
    onTouchStart: (e) => {
      e.preventDefault();
      pressKey(k, true);
    },
    onTouchEnd: () => pressKey(k, false),
    onTouchCancel: () => pressKey(k, false),
    onContextMenu: (e) => e.preventDefault(),
  });

  const showGameHeader =
    phase === "game" &&
    screen !== "intro" &&
    screen !== "complete" &&
    scenes.length > 0;

  const resultShowRetry =
    !lastAnswerCorrect && attemptCount < 2 && lives > 0;

  // if (isPhoneOnly) {
  //   return (
  //     <MobileBlocker
  //       forceBlock={true}
  //       message="هذه الأداة متاحة فقط على أجهزة الكمبيوتر والتابلت. يرجى فتح تعلم بالقصة من لابتوب أو تابلت."
  //       navigationBack="/"
  //     >
  //       {null}
  //     </MobileBlocker>
  //   );
  // }

  if (phase === "form") {
    return (
      <div className={`sl-root ${language === "English" ? "sl-dir-ltr" : ""}`}>
        <Header />
        <div className="sl-inner">
          <h1 className="sl-title-page">{t.pageTitle}</h1>
          <p className="sl-subtitle">{t.pageSubtitle}</p>

          <div className="sl-form-card-wrap">
            <form className="sl-form-card" onSubmit={handleFormSubmit}>
              {apiLoading && (
                <div className="sl-loading-overlay" aria-busy="true">
                  <MiniLoader
                    variant="ai"
                    size={48}
                    color={AI_LOADER_COLOR}
                    speed="normal"
                  />
                  <strong>{t.loading}</strong>
                </div>
              )}

              <div className="sl-field">
                <label className="sl-label" htmlFor="sl-topic">
                  {t.topic}
                </label>
                <input
                  id="sl-topic"
                  className="sl-input"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  disabled={apiLoading}
                />
                {formErrors.topic && (
                  <small style={{ color: "#e17055" }}>{formErrors.topic}</small>
                )}
              </div>

              <div className="sl-field">
                <label className="sl-label" htmlFor="sl-subject">
                  {t.subject}
                </label>
                <input
                  id="sl-subject"
                  className="sl-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={apiLoading}
                />
                {formErrors.subject && (
                  <small style={{ color: "#e17055" }}>{formErrors.subject}</small>
                )}
              </div>

              <div className="sl-field">
                <label className="sl-label" htmlFor="sl-grade">
                  {t.grade}
                </label>
                <select
                  id="sl-grade"
                  className="sl-select"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  disabled={apiLoading || classesLoading}
                >
                  <option value="">
                    {classesLoading
                      ? uiLang === "en"
                        ? "Loading classes..."
                        : "جار تحميل الصفوف..."
                      : uiLang === "en"
                        ? "Select class"
                        : "اختر الصف"}
                  </option>
                  {userClasses.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {formErrors.grade && (
                  <small style={{ color: "#e17055" }}>{formErrors.grade}</small>
                )}
                {classesError && (
                  <small style={{ color: "#e17055" }}>{classesError}</small>
                )}
              </div>

              <div className="sl-field">
                <label className="sl-label" htmlFor="sl-hero">
                  {t.heroName}
                </label>
                <input
                  id="sl-hero"
                  className="sl-input"
                  value={heroName}
                  onChange={(e) => setHeroName(e.target.value)}
                  disabled={apiLoading}
                />
                {formErrors.heroName && (
                  <small style={{ color: "#e17055" }}>{formErrors.heroName}</small>
                )}
              </div>

              <div className="sl-field">
                <label className="sl-label" htmlFor="sl-lang">
                  {t.language}
                </label>
                <select
                  id="sl-lang"
                  className="sl-select"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={apiLoading}
                >
                  <option value="Arabic">Arabic</option>
                  <option value="English">English</option>
                </select>
              </div>

              <div className="sl-field">
                <label className="sl-label" htmlFor="sl-scenes">
                  {t.sceneCount}: {sceneCount}
                </label>
                <div className="sl-range-wrap">
                  <input
                    id="sl-scenes"
                    type="range"
                    className="sl-range"
                    min={3}
                    max={8}
                    value={sceneCount}
                    onChange={(e) => setSceneCount(Number(e.target.value))}
                    disabled={apiLoading}
                  />
                  <span className="sl-range-value">{sceneCount}</span>
                </div>
              </div>

              <button
                type="submit"
                className={`sl-submit${apiLoading ? " sl-submit--loading" : ""}`}
                disabled={apiLoading}
              >
                {apiLoading ? (
                  <>
                    <MiniLoader
                      variant="ai"
                      size={22}
                      color="#ffffff"
                      speed="normal"
                    />
                    <span>{t.loading}</span>
                  </>
                ) : (
                  t.submit
                )}
              </button>
            </form>
          </div>

          {apiError && (
            <div className="sl-error">
              {apiError}
              <div>
                <button
                  type="button"
                  className="sl-retry"
                  onClick={() => setApiError(null)}
                >
                  {t.retry}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Mario render helpers ----------
  const renderHero = () => {
    const phase = heroState.walkPhase || 0;
    const jumping = heroState.jumping;
    const moving = Math.abs(heroState.vx || 0) > 0.4;
    const vy = heroState.vy || 0;

    // Subtle squash/stretch (classic Mario feel)
    let scaleY = 1;
    let scaleXAdj = 1;
    if (jumping) {
      if (vy < -2) {
        // Rising: stretched vertically
        scaleY = 1.08;
        scaleXAdj = 0.94;
      } else if (vy > 2) {
        // Falling: squashed slightly
        scaleY = 0.96;
        scaleXAdj = 1.04;
      }
    }

    // Walk bob (only when moving on ground)
    const bob = !jumping && moving ? Math.abs(Math.sin(phase * 1.2)) * 2 : 0;

    // Mario's running tilt (slight forward lean while moving fast)
    const tiltDeg =
      moving && !jumping ? (heroState.dir < 0 ? 4 : -4) * Math.min(1, Math.abs(heroState.vx || 0) / PHYSICS.MAX_VX) : 0;

    return (
      <div
        className={`mario-hero${jumping ? " mario-hero--jumping" : ""}${moving ? " mario-hero--running" : ""}`}
        style={{
          transform: `translate3d(${heroState.x}px, ${heroState.y - bob}px, 0)`,
        }}
      >
        <div
          className="mario-hero__inner"
          style={{
            transform: `scaleX(${heroState.dir < 0 ? -1 : 1}) scale(${scaleXAdj}, ${scaleY}) rotate(${tiltDeg}deg)`,
          }}
        >
          <div
            className="mario-hero__sprite"
            role="img"
            aria-label="Mario"               
          />
        </div>
      </div>
    );
  };

  const renderArena = () => {
    return (
      <div
        ref={arenaRef}
        className={`mario-arena${arenaShake ? " mario-arena--shake" : ""}${flashLevel === "good" ? " mario-arena--flash-good" : ""}${flashLevel === "bad" ? " mario-arena--flash-bad" : ""}`}
        role="application"
        aria-label="Story Learn Mario level"
      >
        {/* Sky background */}
        <div className="mario-sky" aria-hidden>
          {clouds.map((c) => (
            <div
              key={c.id}
              className="mario-cloud"
              style={{
                top: `${c.top}%`,
                left: `${c.left}%`,
                transform: `scale(${c.scale})`,
                animationDelay: `${c.delay}s`,
              }}
            >
              <span />
              <span />
              <span />
            </div>
          ))}
        </div>

        {/* Hills */}
        <div className="mario-hills" aria-hidden>
          {hills.map((h) => (
            <div
              key={h.id}
              className="mario-hill"
              style={{
                left: `${h.left}%`,
                transform: `scale(${h.scale})`,
                background: `linear-gradient(180deg, hsl(${h.hue}, 55%, 55%) 0%, hsl(${h.hue}, 55%, 38%) 100%)`,
              }}
            />
          ))}
        </div>

        {/* Platforms (challenge flair) */}
        {platformsRef.current.map((p, i) => (
          <div
            key={`plat-${i}`}
            className="mario-platform"
            style={{
              left: `${p.x}px`,
              top: `${p.y}px`,
              width: `${p.w}px`,
              height: `${p.h}px`,
            }}
            aria-hidden
          />
        ))}

        {/* Story: brick obstacle */}
        {obstaclesRef.current.map((o, i) => (
          <div
            key={`obs-${i}`}
            className="mario-brick"
            style={{
              left: `${o.x}px`,
              top: `${o.y}px`,
              width: `${o.w}px`,
              height: `${o.h}px`,
            }}
            aria-hidden
          />
        ))}

        {/* Story: flag */}
        {screen === "story" && flagXRef.current > 0 && (
          <div
            className={`mario-flag${flagReached ? " mario-flag--reached" : ""}${typewriterDone ? "" : " mario-flag--locked"}`}
            style={{ left: `${flagXRef.current}px` }}
            aria-hidden
          >
            <div className="mario-flag__pole" />
            <div className="mario-flag__cloth">🚩</div>
            {!typewriterDone && (
              <div className="mario-flag__hint">{t.needToReadFirst}</div>
            )}
          </div>
        )}

        {/* Challenge: ? blocks (jump into them from below to answer) */}
        {screen === "challenge" &&
          blocksRef.current.map((b, i) => {
            const hit = blockStates[i]?.hit;
            return (
              <div
                key={`q-${i}`}
                className={`mario-question${hit ? " mario-question--hit" : ""}${selectedChoice === i ? (lastAnswerCorrect ? " mario-question--ok" : " mario-question--bad") : ""}`}
                style={{
                  left: `${b.x}px`,
                  top: `${b.y}px`,
                  width: `${b.w}px`,
                  height: `${b.h}px`,
                }}
                aria-hidden
              >
                <span className="mario-question__num">
                  {hit ? "•" : i + 1}
                </span>
              </div>
            );
          })}

        {/* Coins */}
        {coins.map((c) => (
          <div
            key={c.id}
            className={`mario-coin mario-coin--${c.kind}`}
            style={{ left: `${c.x}px`, top: `${c.y}px` }}
          >
            {c.label}
          </div>
        ))}

        {/* Hero */}
        {renderHero()}

        {/* Ground */}
        <div className="mario-ground" aria-hidden />

        {/* Hint */}
        <div className="mario-hint">
          {screen === "challenge" ? t.walkHint : t.storyHint}
        </div>

        {/* On-screen touch controls */}
        <div className="mario-touch" aria-hidden>
          <button
            type="button"
            className="mario-touch__btn mario-touch__btn--left"
            onMouseDown={(e) => {
              e.preventDefault();
              pressKey("left", true);
            }}
            onMouseUp={() => pressKey("left", false)}
            onMouseLeave={() => pressKey("left", false)}
            onTouchStart={(e) => {
              e.preventDefault();
              pressKey("left", true);
            }}
            onTouchEnd={() => pressKey("left", false)}
          >
            ◀
          </button>
          <button
            type="button"
            className="mario-touch__btn mario-touch__btn--right"
            onMouseDown={(e) => {
              e.preventDefault();
              pressKey("right", true);
            }}
            onMouseUp={() => pressKey("right", false)}
            onMouseLeave={() => pressKey("right", false)}
            onTouchStart={(e) => {
              e.preventDefault();
              pressKey("right", true);
            }}
            onTouchEnd={() => pressKey("right", false)}
          >
            ▶
          </button>
          <button
            type="button"
            className="mario-touch__btn mario-touch__btn--jump"
            onMouseDown={(e) => {
              e.preventDefault();
              pressKey("jump", true);
            }}
            onMouseUp={() => pressKey("jump", false)}
            onMouseLeave={() => pressKey("jump", false)}
            onTouchStart={(e) => {
              e.preventDefault();
              pressKey("jump", true);
            }}
            onTouchEnd={() => pressKey("jump", false)}
          >
            ⤒
          </button>
        </div>
      </div>
    );
  };

  const renderConsole = (extraPanel = null) => (
    <div className="ps-console" role="group" aria-label="Story Player Console">
      <div className="ps-console__bar" aria-hidden>
        <span className="ps-console__brand-wrap">
          <img src={botImage} alt="Rafiq" className="ps-console__rafiq-icon" />
          <span className="ps-console__brand">رفيق</span>
        </span>
        <span className="ps-console__dots">
          <span />
          <span />
          <span />
        </span>
        <span className="ps-console__model">امتحاني</span>
      </div>
      <div className="ps-console__screen">
        <div className="ps-console__bezel">
          {renderArena()}
          <div className="ps-console__scan" aria-hidden />
          <div className="ps-console__gloss" aria-hidden />
        </div>
      </div>
      <div className="ps-console__controls">
        <div className="ps-dpad" aria-label="D-Pad">
          <button
            type="button"
            className="ps-dpad__btn ps-dpad__btn--up"
            aria-label="Up / Jump"
            {...holdHandlers("jump")}
          >
            ▲
          </button>
          <button
            type="button"
            className="ps-dpad__btn ps-dpad__btn--left"
            aria-label="Left"
            {...holdHandlers("left")}
          >
            ◀
          </button>
          <div className="ps-dpad__center" aria-hidden />
          <button
            type="button"
            className="ps-dpad__btn ps-dpad__btn--right"
            aria-label="Right"
            {...holdHandlers("right")}
          >
            ▶
          </button>
          <button
            type="button"
            className="ps-dpad__btn ps-dpad__btn--down"
            aria-label="Down"
          >
            ▼
          </button>
        </div>
        <div className="ps-console__center-pad" aria-hidden>
          <span className="ps-console__select">SELECT</span>
          <span className="ps-console__home" />
          <span className="ps-console__start">START</span>
        </div>
        <div className="ps-actions" aria-label="Action buttons">
          <button
            type="button"
            className="ps-action ps-action--triangle"
            aria-label="Triangle"
          >
            △
          </button>
          <button
            type="button"
            className="ps-action ps-action--square"
            aria-label="Square"
          >
            □
          </button>
          <button
            type="button"
            className="ps-action ps-action--circle"
            aria-label="Circle"
          >
            ○
          </button>
          <button
            type="button"
            className="ps-action ps-action--cross"
            aria-label="Cross / Jump"
            {...holdHandlers("jump")}
          >
            ✕
          </button>
        </div>
      </div>
      {extraPanel && <div className="ps-console__extra">{extraPanel}</div>}
      <div className="ps-console__base" aria-hidden>
        <div className="ps-console__speaker">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className="ps-console__speaker">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );

  return (
    <div className={`sl-root ${uiLang === "ar" ? "" : "sl-dir-ltr"}`}>
      <Header />
      <div className="sl-inner sl-game">
        {showGameHeader && (
          <header className="sl-game-header">
            <span className="sl-stat sl-stat--score">
              <span className="sl-stat__pill">
                ⚡ {score} {t.xpLabel}
              </span>
            </span>
            <span
              className={`sl-stat sl-stat--timer${
                screen === "challenge" && !isLocked && timeLeft <= 10
                  ? " sl-stat--timer-low"
                  : ""
              }${
                screen === "challenge" && !isLocked
                  ? " sl-stat--timer-active"
                  : " sl-stat--timer-paused"
              }`}
              aria-label={t.timeLabel}
            >
              <span className="sl-stat__pill">
                ⏱ {String(Math.floor(timeLeft / 60)).padStart(1, "0")}:
                {String(timeLeft % 60).padStart(2, "0")}
              </span>
            </span>
            <div className="sl-hearts" aria-label={t.livesLabel}>
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={`sl-heart ${i < lives ? "sl-heart--full" : "sl-heart--empty"}`}
                >
                  ♥
                </span>
              ))}
            </div>
            <div className="sl-progress-wrap">
              <span className="sl-progress-label">
                {t.sceneOf(currentSceneIndex + 1, scenes.length)}
              </span>
              <div className="sl-progress-track">
                <div
                  className="sl-progress-fill"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </header>
        )}

        <div
          className={`sl-screen ${screenOpacity ? "sl-screen--visible" : "sl-screen--hidden"}`}
          style={{ opacity: screenOpacity }}
        >
          {screen === "intro" && meta && (
            <div className="sl-card sl-card--centered sl-card--intro">
              <div className="sl-intro-bg" aria-hidden />
              <h2 className="sl-world-title">{meta.world}</h2>
              <p className="sl-welcome">
                {t.welcome(meta.hero || meta.title || "", meta.world || "")}
              </p>
              <div className="sl-badge">
                {t.topicBadge}: {meta.topic}
              </div>
              <button
                type="button"
                className="sl-btn-primary sl-btn-pulse"
                onClick={() => setScreen("story")}
              >
                {t.introCta}
              </button>
              <div className="sl-key-hints">
                <span>
                  <kbd>{t.keyEnter}</kbd> / <kbd>{t.keySpace}</kbd>
                </span>
              </div>
            </div>
          )}

          {screen === "story" && currentScene && (
            <div className="sl-card sl-card--mario-card">
              <div className="mario-banner mario-banner--story">
                <span className="mario-banner__title">
                  {t.sceneOf(currentSceneIndex + 1, scenes.length)}
                </span>
                <p className="mario-banner__text">
                  {narrativeFull.slice(0, typewriterIndex)}
                  {!typewriterDone && (
                    <span className="sl-caret" aria-hidden>
                      ▍
                    </span>
                  )}
                </p>
              </div>
              {renderConsole()}
              <div className="sl-story-actions">
                {!typewriterDone ? (
                  <button
                    type="button"
                    className="sl-btn-secondary"
                    onClick={skipTypewriter}
                  >
                    {t.skip}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="sl-btn-primary"
                    onClick={goToChallenge}
                  >
                    {t.continueReading}
                  </button>
                )}
              </div>
            </div>
          )}

          {screen === "challenge" && currentScene && (
            <div className="sl-card sl-card--mario-card">
              <div className="mario-banner mario-banner--challenge">
                <span className="mario-banner__title">
                  {t.questionPrefix}
                </span>
                <p className="mario-banner__text">{currentScene.challenge}</p>
              </div>
              {renderConsole(
                <div className="sl-choices-panel sl-choices-panel--readonly">
                  {(currentScene.choices || []).slice(0, 4).map((c, idx) => (
                    <div
                      key={idx}
                      className={`sl-choice-row sl-choice-row--readonly${selectedChoice === idx ? (lastAnswerCorrect ? " sl-choice-row--correct" : " sl-choice-row--wrong") : ""}`}
                      aria-disabled="true"
                    >
                      <span className="sl-choice-row__num">{idx + 1}</span>
                      <span className="sl-choice-row__text">{c}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {screen === "result" && currentScene && (
            <div
              className={`sl-card sl-result-card ${lastAnswerCorrect ? "sl-result-card--ok" : "sl-result-card--bad"}`}
            >
              <h3
                className={`sl-result-title ${lastAnswerCorrect ? "sl-result-title--ok" : "sl-result-title--bad"}`}
              >
                {lastAnswerCorrect
                  ? t.correct
                  : timeLeft === 0 && selectedChoice === null
                    ? t.timeUp
                    : t.wrong}
              </h3>
              <p className="sl-explanation">{currentScene.explanation}</p>
              <button
                type="button"
                className="sl-btn-primary"
                onClick={handleResultAction}
              >
                {resultShowRetry ? t.tryAgain : t.continue}
              </button>
              <div className="sl-key-hints">
                <span>
                  <kbd>{t.keyEnter}</kbd> / <kbd>{t.keySpace}</kbd>
                </span>
              </div>
            </div>
          )}

          {screen === "reward" && currentScene && (
            <div className="sl-card">
              <p className="sl-reward-text">{currentScene.reward_text}</p>
              <div className="sl-xp-badge">{t.xpGain(rewardXpEarned)}</div>
              <button
                type="button"
                className="sl-btn-primary"
                onClick={handleRewardNext}
              >
                {t.next}
              </button>
              <div className="sl-key-hints">
                <span>
                  <kbd>{t.keyEnter}</kbd> / <kbd>{t.keySpace}</kbd>
                </span>
              </div>
            </div>
          )}

          {screen === "complete" && meta && (
            <div className="sl-card sl-card--centered">
              <div className="sl-festival" aria-hidden>
                <div className="sl-festival__firework sl-festival__firework--one" />
                <div className="sl-festival__firework sl-festival__firework--two" />
                <div className="sl-festival__firework sl-festival__firework--three" />
                <span className="sl-festival__balloon sl-festival__balloon--red">🎈</span>
                <span className="sl-festival__balloon sl-festival__balloon--gold">🎈</span>
                {Array.from({ length: 18 }).map((_, i) => (
                  <span
                    key={`party-${i}`}
                    className={`sl-festival__confetti sl-festival__confetti--${
                      (i % 6) + 1
                    }`}
                    style={{
                      left: `${(i * 13) % 100}%`,
                      animationDelay: `${(i % 9) * 0.12}s`,
                    }}
                  />
                ))}
              </div>
              <div className="sl-complete-emoji" aria-hidden>
                🎉
              </div>
              <h2 className="sl-world-title" style={{ fontSize: "1.5rem" }}>
                {t.completeTitle}
              </h2>
              <div className="sl-stars">{"⭐".repeat(starCount)}</div>
              <p style={{ fontWeight: 800, color: "#6c5ce7" }}>
                {t.finalScore}: {score} {t.xpLabel}
              </p>
              <p className="sl-moral">
                <strong>{t.moral}:</strong> {meta.moral}
              </p>
              <div className="sl-actions-row">
                <button
                  type="button"
                  className="sl-btn-primary"
                  onClick={handleReplaySameStory}
                >
                  {t.playAgain}
                </button>
                <button
                  type="button"
                  className="sl-btn-secondary"
                  onClick={handleBackToForm}
                >
                  {t.backToForm}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryLearnComponent;
